import React, { useState, useRef, useEffect } from 'react';
import api from '../../services/api';
import PollMessage from './PollMessage';

function fmtSec(s) {
    if (!Number.isFinite(s) || s < 0) s = 0;
    return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
}

/**
 * Кастомный плеер голосовых.
 * Длительность берём из метаданных (attachment.durationSec) — у MediaRecorder WebM
 * её обычно нет в файле, и нативный <audio> показывает 0:00 / Infinity.
 */
function VoicePlayer({ attachment }) {
    const [playing, setPlaying] = useState(false);
    const [pos, setPos] = useState(0);            // текущая позиция, сек
    const audioRef = useRef(null);
    const totalSec = attachment.durationSec || 0;

    useEffect(() => {
        const a = audioRef.current;
        if (!a) return;
        const onTime = () => setPos(a.currentTime || 0);
        const onEnd = () => { setPlaying(false); setPos(0); a.currentTime = 0; };
        const onPlay = () => setPlaying(true);
        const onPause = () => setPlaying(false);
        a.addEventListener('timeupdate', onTime);
        a.addEventListener('ended', onEnd);
        a.addEventListener('play', onPlay);
        a.addEventListener('pause', onPause);
        return () => {
            a.removeEventListener('timeupdate', onTime);
            a.removeEventListener('ended', onEnd);
            a.removeEventListener('play', onPlay);
            a.removeEventListener('pause', onPause);
        };
    }, []);

    const toggle = () => {
        const a = audioRef.current;
        if (!a) return;
        if (a.paused) a.play().catch(() => {});
        else a.pause();
    };

    const seek = (e) => {
        const a = audioRef.current;
        if (!a || !totalSec) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
        a.currentTime = ratio * totalSec;
        setPos(a.currentTime);
    };

    const progressPct = totalSec ? Math.min(100, (pos / totalSec) * 100) : 0;
    const remaining = totalSec ? Math.max(0, totalSec - pos) : 0;

    return (
        <div className="pchat-voice">
            <button type="button" className="pchat-voice-play" onClick={toggle} title={playing ? 'Пауза' : 'Играть'}>
                {playing ? '⏸' : '▶'}
            </button>
            <div className="pchat-voice-body">
                <div className="pchat-voice-bar" onClick={seek}>
                    <div className="pchat-voice-fill" style={{ width: `${progressPct}%` }} />
                </div>
                <div className="pchat-voice-time">
                    {totalSec ? fmtSec(playing || pos > 0 ? pos : remaining) : '🎤 голосовое'}
                    {totalSec ? ` / ${fmtSec(totalSec)}` : ''}
                </div>
            </div>
            <audio
                ref={audioRef}
                preload="metadata"
                src={api.chat.fileUrl(attachment.fileId)}
                style={{ display: 'none' }}
            />
        </div>
    );
}

function fmtTime(d) {
    if (!d) return '';
    const dt = new Date(d);
    const today = new Date();
    const sameDay = dt.toDateString() === today.toDateString();
    if (sameDay) return dt.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    return dt.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function highlightText(text, currentLogin) {
    if (!text) return null;
    const parts = text.split(/(@[A-Za-z0-9_.\-]{2,40})/g);
    return parts.map((p, i) => {
        if (p.startsWith('@')) {
            const isMe = p.slice(1) === currentLogin;
            return <span key={i} className="mention" style={isMe ? { background: 'rgba(255,209,102,0.25)', color: '#ffd166' } : null}>{p}</span>;
        }
        return <React.Fragment key={i}>{p}</React.Fragment>;
    });
}

export default function MessageItem({
    message, currentLogin, isAdmin,
    onReply, onShowReaders, onPin, onDelete, onEdit,
    onShowImage, onScrollToReply, allMessagesById
}) {
    const own = message.fromLogin === currentLogin;
    const isSystem = message.type === 'system' || message.deletedAt;
    const [showReact, setShowReact] = useState(false);
    const [showActions, setShowActions] = useState(false); // для мобилки (tap)

    if (isSystem) {
        return <div className="pchat-msg system">{message.text || '[system]'}</div>;
    }

    // На тач-устройствах :hover не срабатывает — раскрываем actions по тапу.
    // Класс .show-actions активирует их через CSS только в мобильном @media.
    const handleBubbleClick = (e) => {
        // не закрываем при клике по самим кнопкам действий / ссылкам / картинкам
        const tag = (e.target.tagName || '').toLowerCase();
        if (['button', 'a', 'img'].includes(tag)) return;
        setShowActions(s => !s);
    };

    const handleReact = async (emoji) => {
        try { await api.chat.react(message._id, emoji); } catch (_) {}
        setShowReact(false);
    };
    const handleEdit = () => {
        const t = window.prompt('Редактировать сообщение:', message.text || '');
        if (t != null && t.trim() && t !== message.text) onEdit(message._id, t.trim());
    };

    const reactionsAgg = {};
    (message.reactions || []).forEach(r => {
        if (!reactionsAgg[r.emoji]) reactionsAgg[r.emoji] = { count: 0, mine: false };
        reactionsAgg[r.emoji].count++;
        if (r.userLogin === currentLogin) reactionsAgg[r.emoji].mine = true;
    });

    const replyMsg = message.replyTo ? allMessagesById?.get(String(message.replyTo)) : null;

    return (
        <div
            className={`pchat-msg ${own ? 'own' : ''} ${showActions ? 'show-actions' : ''}`}
            data-msg-id={message._id}
            onClick={handleBubbleClick}
        >
            <div className="pchat-actions-pop">
                <button title="Ответить" onClick={() => { onReply(message); setShowActions(false); }}>↩</button>
                <button title="Реакция" onClick={() => setShowReact(s => !s)}>😊</button>
                {own && message.type === 'text' && <button title="Редактировать" onClick={handleEdit}>✎</button>}
                {isAdmin && <button title={message.pinned ? 'Открепить' : 'Закрепить'} onClick={() => onPin(message._id)}>📌</button>}
                <button title="Кто прочитал" onClick={() => onShowReaders(message._id)}>👁</button>
                {isAdmin && <button className="danger" title="Удалить" onClick={() => onDelete(message._id)}>🗑</button>}
            </div>
            {showReact && (
                <div className="pchat-actions-pop" style={{ display: 'flex', top: '-50px' }}>
                    {['👍', '❤️', '😂', '😮', '😢', '👏', '🔥'].map(e =>
                        <button key={e} onClick={() => handleReact(e)}>{e}</button>
                    )}
                </div>
            )}

            <div className="pchat-msg-head">
                <span className={`pchat-msg-from ${message.fromRole === 'admin' ? 'admin' : ''}`}>
                    {message.fromLogin}{message.fromRole === 'admin' ? ' • админ' : ''}
                </span>
                {message.pinned && <span title="Закреплено">📌</span>}
            </div>

            {replyMsg && (
                <div className="pchat-reply" onClick={() => onScrollToReply && onScrollToReply(message.replyTo)}>
                    <strong>{replyMsg.fromLogin}:</strong>{' '}
                    {(replyMsg.text
                        || (replyMsg.type === 'image' ? '📷 Изображение'
                            : replyMsg.type === 'audio' ? '🎤 Голосовое'
                            : replyMsg.type === 'file' ? '📎 Файл'
                            : replyMsg.type === 'poll' ? '📊 Опрос' : '')
                    ).slice(0, 120)}
                </div>
            )}

            {message.type === 'image' && message.attachment && (
                <>
                    <img
                        className="pchat-image"
                        src={api.chat.thumbUrl(message.attachment.thumbnailId || message.attachment.fileId)}
                        alt={message.attachment.originalName || ''}
                        onClick={() => onShowImage(api.chat.fileUrl(message.attachment.fileId))}
                        onError={(e) => { e.target.src = api.chat.fileUrl(message.attachment.fileId); }}
                    />
                    {message.text && <div className="pchat-msg-text">{highlightText(message.text, currentLogin)}</div>}
                </>
            )}

            {message.type === 'file' && message.attachment && (
                <a className="pchat-file" href={api.chat.fileUrl(message.attachment.fileId)} target="_blank" rel="noreferrer" download={message.attachment.originalName}>
                    <span className="pchat-file-icon">📎</span>
                    <span>
                        <div className="pchat-file-name">{message.attachment.originalName || 'файл'}</div>
                        <div className="pchat-file-size">{(message.attachment.size / 1024).toFixed(1)} КБ</div>
                    </span>
                </a>
            )}

            {message.type === 'audio' && message.attachment && (
                <VoicePlayer attachment={message.attachment} />
            )}

            {message.type === 'text' && (
                <div className="pchat-msg-text">{highlightText(message.text, currentLogin)}</div>
            )}

            {message.type === 'poll' && message.poll && (
                <PollMessage message={message} currentLogin={currentLogin} />
            )}

            {Object.keys(reactionsAgg).length > 0 && (
                <div className="pchat-reactions">
                    {Object.entries(reactionsAgg).map(([emoji, agg]) => (
                        <span key={emoji} className={`pchat-react ${agg.mine ? 'mine' : ''}`} onClick={() => handleReact(emoji)}>
                            {emoji} {agg.count}
                        </span>
                    ))}
                </div>
            )}

            <div className="pchat-msg-meta">
                <span>{fmtTime(message.createdAt)}</span>
                {message.editedAt && <span>(изменено)</span>}
                {(message.readBy?.length > 0) && (
                    <span className="read-link" onClick={() => onShowReaders(message._id)} title="Кто прочитал">
                        ✓✓ {message.readBy.length}
                    </span>
                )}
            </div>
        </div>
    );
}
